import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/life-insurance-for-seniors-over-60-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Senior Rate Finder Data ────────────────────────────────────────────── */

type SeniorAge = '60' | '65' | '70' | '75' | '80';
type SeniorHealth = 'excellent' | 'good' | 'fair' | 'serious';
type SeniorGoal = 'burial' | 'legacy' | 'income' | 'mortgage' | 'debt';

interface SeniorRateResult {
  product: string;
  carrier: string;
  coverage: string;
  monthlyRate: string;
  approval: string;
  amBest: string;
  noExam: boolean;
  highlight: boolean;
  badge?: string;
  note: string;
}

const seniorRates: Record<SeniorAge, Record<SeniorHealth, SeniorRateResult[]>> = {
  '60': {
    excellent: [
      { product: '20-Year Term Life', carrier: 'Banner Life', coverage: '$500,000', monthlyRate: '$88/mo', approval: '24–48 hrs', amBest: 'A+', noExam: true, highlight: true, badge: 'Best Value', note: 'No exam · Preferred health class' },
      { product: 'Simplified Issue Whole Life', carrier: 'North American', coverage: '$100,000', monthlyRate: '$142/mo', approval: '48 hrs', amBest: 'A+', noExam: true, highlight: false, note: 'Permanent · Cash value growth' },
      { product: 'Final Expense Whole Life', carrier: 'Mutual of Omaha', coverage: '$25,000', monthlyRate: '$52/mo', approval: '24 hrs', amBest: 'A+', noExam: true, highlight: false, note: 'No exam · Covers burial costs' },
    ],
    good: [
      { product: '15-Year Term Life', carrier: 'Protective Life', coverage: '$250,000', monthlyRate: '$78/mo', approval: '48 hrs', amBest: 'A+', noExam: false, highlight: true, badge: 'Best Value', note: 'Controlled conditions accepted' },
      { product: 'Final Expense Whole Life', carrier: 'Foresters Financial', coverage: '$20,000', monthlyRate: '$46/mo', approval: '48 hrs', amBest: 'A', noExam: true, highlight: false, note: 'No exam · Most popular for this age' },
      { product: 'Simplified Issue Whole Life', carrier: 'Americo', coverage: '$50,000', monthlyRate: '$88/mo', approval: '48 hrs', amBest: 'A', noExam: true, highlight: false, note: 'No exam · Permanent coverage' },
    ],
    fair: [
      { product: 'Final Expense Whole Life', carrier: 'Transamerica', coverage: '$15,000', monthlyRate: '$58/mo', approval: '48 hrs', amBest: 'A', noExam: true, highlight: true, badge: 'Best Match', note: 'Accepts most health conditions' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'Mutual of Omaha', coverage: '$15,000', monthlyRate: '$68/mo', approval: 'Instant', amBest: 'A+', noExam: true, highlight: false, note: 'No health questions' },
    ],
    serious: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'AIG', coverage: '$15,000', monthlyRate: '$72/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: true, badge: 'Guaranteed Approval', note: 'No questions · Any health condition' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', coverage: '$10,000', monthlyRate: '$68/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: false, note: 'Guaranteed acceptance ages 50–80' },
    ],
  },
  '65': {
    excellent: [
      { product: '10-Year Term Life', carrier: 'Banner Life', coverage: '$250,000', monthlyRate: '$96/mo', approval: '24–48 hrs', amBest: 'A+', noExam: true, highlight: true, badge: 'Best Value', note: 'No exam · Preferred health class' },
      { product: 'Final Expense Whole Life', carrier: 'Mutual of Omaha', coverage: '$25,000', monthlyRate: '$62/mo', approval: '24 hrs', amBest: 'A+', noExam: true, highlight: false, note: 'No exam · Permanent coverage' },
      { product: 'Simplified Issue Whole Life', carrier: 'North American', coverage: '$50,000', monthlyRate: '$118/mo', approval: '48 hrs', amBest: 'A+', noExam: true, highlight: false, note: 'No exam · Cash value growth' },
    ],
    good: [
      { product: 'Final Expense Whole Life', carrier: 'Foresters Financial', coverage: '$20,000', monthlyRate: '$58/mo', approval: '48 hrs', amBest: 'A', noExam: true, highlight: true, badge: 'Most Popular', note: 'No exam · Controlled conditions OK' },
      { product: 'Simplified Issue Whole Life', carrier: 'Americo', coverage: '$35,000', monthlyRate: '$98/mo', approval: '48 hrs', amBest: 'A', noExam: true, highlight: false, note: 'No exam · Permanent coverage' },
    ],
    fair: [
      { product: 'Final Expense Whole Life', carrier: 'Transamerica', coverage: '$12,000', monthlyRate: '$64/mo', approval: '48 hrs', amBest: 'A', noExam: true, highlight: true, badge: 'Best Match', note: 'Accepts most health conditions' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'Mutual of Omaha', coverage: '$12,000', monthlyRate: '$78/mo', approval: 'Instant', amBest: 'A+', noExam: true, highlight: false, note: 'No health questions' },
    ],
    serious: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'AIG', coverage: '$12,000', monthlyRate: '$84/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: true, badge: 'Guaranteed Approval', note: 'No questions · Any health condition' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', coverage: '$10,000', monthlyRate: '$78/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: false, note: 'Guaranteed acceptance' },
    ],
  },
  '70': {
    excellent: [
      { product: 'Final Expense Whole Life', carrier: 'Mutual of Omaha', coverage: '$20,000', monthlyRate: '$72/mo', approval: '24 hrs', amBest: 'A+', noExam: true, highlight: true, badge: 'Best Value', note: 'No exam · Best rate for age 70' },
      { product: 'Simplified Issue Whole Life', carrier: 'Foresters', coverage: '$25,000', monthlyRate: '$98/mo', approval: '48 hrs', amBest: 'A', noExam: true, highlight: false, note: 'No exam · Permanent coverage' },
    ],
    good: [
      { product: 'Final Expense Whole Life', carrier: 'Transamerica', coverage: '$15,000', monthlyRate: '$78/mo', approval: '48 hrs', amBest: 'A', noExam: true, highlight: true, badge: 'Most Popular', note: 'No exam · Controlled conditions OK' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'Mutual of Omaha', coverage: '$12,000', monthlyRate: '$88/mo', approval: 'Instant', amBest: 'A+', noExam: true, highlight: false, note: 'No health questions' },
    ],
    fair: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'AIG', coverage: '$10,000', monthlyRate: '$92/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: true, badge: 'Best Match', note: 'Guaranteed acceptance' },
    ],
    serious: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', coverage: '$10,000', monthlyRate: '$96/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: true, badge: 'Guaranteed Approval', note: 'No questions · Any health condition' },
    ],
  },
  '75': {
    excellent: [
      { product: 'Final Expense Whole Life', carrier: 'Mutual of Omaha', coverage: '$15,000', monthlyRate: '$98/mo', approval: '48 hrs', amBest: 'A+', noExam: true, highlight: true, badge: 'Best Available', note: 'No exam · Best rate for age 75' },
      { product: 'Guaranteed Issue Whole Life', carrier: 'AIG', coverage: '$10,000', monthlyRate: '$108/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: false, note: 'No health questions' },
    ],
    good: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Mutual of Omaha', coverage: '$10,000', monthlyRate: '$112/mo', approval: 'Instant', amBest: 'A+', noExam: true, highlight: true, badge: 'Most Popular', note: 'No health questions' },
    ],
    fair: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', coverage: '$10,000', monthlyRate: '$118/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: true, badge: 'Guaranteed Approval', note: 'Guaranteed acceptance' },
    ],
    serious: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', coverage: '$8,000', monthlyRate: '$122/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: true, badge: 'Guaranteed Approval', note: 'No questions · Any health condition' },
    ],
  },
  '80': {
    excellent: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'AIG', coverage: '$10,000', monthlyRate: '$138/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: true, badge: 'Best Available', note: 'No health questions · Age 80 max' },
    ],
    good: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Mutual of Omaha', coverage: '$10,000', monthlyRate: '$142/mo', approval: 'Instant', amBest: 'A+', noExam: true, highlight: true, badge: 'Most Popular', note: 'No health questions' },
    ],
    fair: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', coverage: '$8,000', monthlyRate: '$148/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: true, badge: 'Guaranteed Approval', note: 'Guaranteed acceptance' },
    ],
    serious: [
      { product: 'Guaranteed Issue Whole Life', carrier: 'Gerber Life', coverage: '$7,500', monthlyRate: '$152/mo', approval: 'Instant', amBest: 'A', noExam: true, highlight: true, badge: 'Guaranteed Approval', note: 'No questions · Any health condition' },
    ],
  },
};

const FAQS = [
  { q: 'Can I get life insurance at age 60, 65, 70, or 75 in Indianapolis?', a: 'Yes. Indianapolis seniors can get life insurance at any age from 60 to 85. At age 60–65, term life insurance is still available from carriers like Banner Life and Protective Life. At age 65–75, final expense and simplified issue whole life are the most popular options. At age 75–85, guaranteed issue whole life is available with no health questions and instant approval. The key is working with an independent agent who knows which carriers accept which ages and health conditions.' },
  { q: 'What is the best life insurance for seniors over 60 in Indianapolis?', a: 'For Indianapolis seniors over 60, the best life insurance depends on your health and goal: Excellent health (60–70): Term life from Banner Life or Protective Life offers the highest coverage at the lowest cost. Good health (60–80): Final expense whole life from Mutual of Omaha or Foresters Financial — no exam, permanent coverage, $10K–$35K. Fair health (60–85): Simplified issue final expense from Transamerica — accepts most controlled conditions. Any health (50–85): Guaranteed issue whole life from AIG or Gerber Life — no questions, instant approval.' },
  { q: 'How much does life insurance cost for a 65-year-old in Indianapolis?', a: 'Life insurance costs for a 65-year-old in Indianapolis: Final expense whole life (no exam): $58–$78/month for $15,000–$20,000 of coverage. Simplified issue whole life (no exam): $98–$118/month for $35,000–$50,000 of coverage. Guaranteed issue whole life (no questions): $78–$88/month for $10,000–$12,000 of coverage. 10-year term life (exam required): $96–$140/month for $250,000 of coverage. Rates vary by health class and carrier. As an independent agent, we compare all carriers to find your lowest rate.' },
  { q: 'Is there a waiting period for senior life insurance in Indianapolis?', a: 'It depends on the type: Final expense (simplified issue): No waiting period for accidental death. 2-year graded benefit for natural causes — if you die in the first 2 years, beneficiaries receive premiums paid plus 10% interest. After 2 years, full death benefit is paid. Guaranteed issue: Always has a 2-year waiting period for natural causes. Term life (fully underwritten): No waiting period — full coverage from day one. Most Indianapolis seniors choose final expense or guaranteed issue, so the 2-year graded benefit is important to understand.' },
  { q: 'Can I get life insurance at 70 or 75 in Indianapolis without a medical exam?', a: 'Yes. Indianapolis seniors ages 70–75 can get life insurance without a medical exam through: Final expense simplified issue (ages 50–80): Answer 10–15 health questions, no exam. Approval in 24–48 hours. Coverage up to $35,000. Guaranteed issue whole life (ages 50–85): No health questions at all. Instant approval. Coverage up to $25,000. Both products are permanent whole life insurance — coverage never expires and premiums never increase. Mutual of Omaha, Foresters, and AIG are the top carriers for Indianapolis seniors in this age range.' },
  { q: 'What is final expense insurance and how does it work for Indianapolis seniors?', a: 'Final expense insurance is a type of permanent whole life insurance designed specifically for seniors to cover burial, funeral, and end-of-life costs. The average funeral in Indianapolis costs $8,000–$12,000. A final expense policy provides $5,000–$35,000 of coverage with no medical exam — just health questions answered by phone or online. Premiums are fixed and never increase. Coverage never expires. The death benefit is paid directly to your beneficiary (usually a family member) who uses it to cover funeral costs, medical bills, and other final expenses. Policies start at $28–$45/month for Indianapolis seniors.' },
  { q: 'Does Social Security pay for burial costs in Indianapolis?', a: 'Social Security pays a one-time death benefit of only $255 — far less than the $8,000–$12,000 average funeral cost in Indianapolis. This $255 benefit has not been updated since 1954. Without life insurance, your family is responsible for the full cost of your funeral, burial, and any outstanding medical bills. A final expense policy from $28–$45/month ensures your family isn\'t left with this financial burden.' },
  { q: 'Can Indianapolis seniors with diabetes or heart disease get life insurance?', a: 'Yes. Many Indianapolis seniors with diabetes, heart disease, COPD, or other conditions can still get life insurance. Controlled type 2 diabetes: Simplified issue final expense from Transamerica or Foresters typically accepts well-controlled diabetes. Controlled heart disease (no recent events): Some simplified issue carriers accept stable heart conditions. Recent heart attack or stroke (within 2 years): Guaranteed issue whole life accepts any condition with no questions. Active cancer treatment: Guaranteed issue is typically the only option. An independent agent can match your specific health profile to the right carrier.' },
  { q: 'How much life insurance does a senior over 60 need in Indianapolis?', a: 'The right amount of life insurance for an Indianapolis senior over 60 depends on your goal: Burial and funeral costs: $10,000–$15,000 covers the average Indianapolis funeral. Final expenses (medical bills, debts): $15,000–$25,000. Legacy for children or grandchildren: $25,000–$100,000. Income replacement for a surviving spouse: $100,000–$500,000. Mortgage payoff: Equal to remaining mortgage balance. Most Indianapolis seniors over 60 focus on final expense coverage ($10,000–$25,000) to ensure their family isn\'t burdened with end-of-life costs.' },
  { q: 'How do I get a senior life insurance quote in Indianapolis?', a: 'Call Hoosier Life Insurance at (317) 648-2983 or use the Senior Rate Finder on this page. As an independent agent, Benjamin Wright compares all available carriers for your age and health situation — Mutual of Omaha, Foresters, Transamerica, AIG, Gerber Life, and more. Most Indianapolis seniors receive their quote within 24 hours. No obligation, no pressure, no cost.' },
];

export default function LifeInsuranceForSeniorsOver60IndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [seniorAge, setSeniorAge] = useState<SeniorAge>('65');
  const [seniorHealth, setSeniorHealth] = useState<SeniorHealth>('good');
  const [rateResults, setRateResults] = useState<SeniorRateResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Life Insurance for Seniors Over 60 Indianapolis IN | Senior Rate Finder | Hoosier Life';
    const description = 'Life insurance for Indianapolis seniors over 60, 65, 70, 75, and 80. Senior Rate Finder tool. Final expense, guaranteed issue & simplified issue. No medical exam. Compare 20+ carriers — free.';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: 'life insurance for seniors over 60 Indianapolis, senior life insurance Indianapolis, life insurance for 65 year old Indianapolis, final expense insurance Indianapolis Indiana' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];
    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });
    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': 'WebPage', name: title, description, url: CANONICAL, dateModified: '2026-04-15T00:00:00Z' },
        { '@type': 'FAQPage', mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
        { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL }, { '@type': 'ListItem', position: 2, name: 'Life Insurance for Seniors Over 60 Indianapolis', item: CANONICAL }] },
      ],
    };
    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schema);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const handleFindRates = () => {
    const results = seniorRates[seniorAge]?.[seniorHealth] || [];
    setRateResults(results);
  };

  const healthLabels: Record<SeniorHealth, string> = {
    excellent: 'Excellent — No major conditions',
    good: 'Good — 1–2 controlled conditions',
    fair: 'Fair — Several health issues',
    serious: 'Serious — Multiple conditions',
  };

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Life Insurance for Seniors Over 60 Indianapolis</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/3bbdb69a960d0f290b0c85b3fa57b58b.jpg"
            alt="Life insurance for seniors over 60 in Indianapolis Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/95 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-user-heart-line text-sm"></i>
              Indianapolis Seniors · Ages 60–85 · No Medical Exam Required
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Life Insurance for Seniors<br />
              <span className="text-[#D97642]">Over 60 — Indianapolis, IN</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Final expense, guaranteed issue, and simplified issue life insurance for Indianapolis seniors ages 60–85. <strong className="text-white">No medical exam required.</strong> Use our <strong className="text-white">Senior Rate Finder</strong> to see your exact options and rates.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: 'Ages 60–85', label: 'Eligibility range' },
                { value: '$28/mo', label: 'Starting rate' },
                { value: '24 hrs', label: 'Typical approval' },
                { value: '0', label: 'Medical exams required' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#rate-finder" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-search-line text-lg"></i>
                Find My Senior Rate
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content + Sidebar */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT */}
          <div>

            {/* Senior Rate Finder */}
            <div id="rate-finder" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Interactive Tool</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Senior Rate Finder — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age and health situation to see the best life insurance options available to you as an Indianapolis senior — with carrier, coverage amount, and monthly rate.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-user-heart-line text-[#D97642]"></i>
                    Your Profile
                  </h3>

                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {(['60', '65', '70', '75', '80'] as SeniorAge[]).map(age => (
                        <button key={age} onClick={() => { setSeniorAge(age); setRateResults(null); }}
                          className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${seniorAge === age ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          Age {age}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Health Situation</label>
                    <div className="grid grid-cols-2 gap-2">
                      {([
                        { value: 'excellent', label: 'Excellent', desc: 'No major conditions' },
                        { value: 'good', label: 'Good', desc: '1–2 controlled conditions' },
                        { value: 'fair', label: 'Fair', desc: 'Several health issues' },
                        { value: 'serious', label: 'Serious', desc: 'Multiple conditions' },
                      ] as { value: SeniorHealth; label: string; desc: string }[]).map(opt => (
                        <button key={opt.value} onClick={() => { setSeniorHealth(opt.value); setRateResults(null); }}
                          className={`px-4 py-3 rounded-lg text-left transition-all cursor-pointer ${seniorHealth === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${seniorHealth === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={handleFindRates}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-search-line text-lg"></i>
                    Find My Senior Life Insurance Rates
                  </button>
                </div>

                {rateResults !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        {rateResults.length} Options — Age {seniorAge} · {healthLabels[seniorHealth]}
                      </h3>
                    </div>
                    <p className="text-xs text-gray-400 mb-6">Estimated rates. Actual rates determined after full application review.</p>

                    <div className="flex flex-col gap-4">
                      {rateResults.map((r, i) => (
                        <div key={i} className={`rounded-xl border-2 p-5 ${r.highlight ? 'border-[#D97642] bg-[#FFF5EF]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                            <div>
                              {r.badge && <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full mb-2 ${r.highlight ? 'bg-[#D97642] text-white' : 'bg-[#1A3A52] text-white'}`}>{r.badge}</span>}
                              <div className="font-bold text-[#1A3A52]">{r.product}</div>
                              <div className="text-sm text-gray-500 mt-0.5">{r.carrier} · AM Best {r.amBest}</div>
                              <div className="text-xs text-gray-400 mt-0.5">Approval: {r.approval} · {r.noExam ? 'No exam required' : 'Exam may be required'}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-extrabold text-[#D97642]">{r.monthlyRate}</div>
                              <div className="text-sm text-gray-500">{r.coverage} coverage</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#D97642] font-semibold">
                            <i className="ri-information-line"></i>
                            {r.note}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-5 p-4 bg-[#1A3A52] rounded-xl text-center">
                      <p className="text-white text-sm mb-3">Get your exact personalized senior rate — free, no obligation.</p>
                      <button onClick={() => setShowBookingModal(true)}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer">
                        <i className="ri-calendar-check-fill"></i>
                        Get My Exact Senior Rate — Free
                      </button>
                    </div>
                  </div>
                )}

                {rateResults === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-user-heart-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age and health situation above, then click <strong className="text-[#1A3A52]">Find My Senior Life Insurance Rates</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Why Seniors Need Life Insurance */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Why It Matters</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Why Indianapolis Seniors Over 60 Need Life Insurance
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-heart-line', title: 'Burial & Funeral Costs', desc: 'The average Indianapolis funeral costs $8,000–$12,000. Without coverage, your family pays this out of pocket — often within days of your death.' },
                  { icon: 'ri-bill-line', title: 'Medical Bills & Debts', desc: 'End-of-life medical bills average $39,000. Life insurance ensures these debts don\'t become your family\'s burden.' },
                  { icon: 'ri-home-heart-line', title: 'Protect a Surviving Spouse', desc: 'If your spouse depends on your Social Security or pension, life insurance replaces that income when you\'re gone.' },
                  { icon: 'ri-community-line', title: 'Leave a Legacy', desc: 'Even a $25,000 policy creates a meaningful inheritance for your children or grandchildren.' },
                  { icon: 'ri-shield-check-line', title: 'Peace of Mind', desc: 'Knowing your family won\'t face financial hardship during an already difficult time is priceless.' },
                  { icon: 'ri-time-line', title: 'It\'s Not Too Late', desc: 'Indianapolis seniors up to age 85 can still get meaningful coverage. Guaranteed issue accepts any health condition.' },
                ].map(item => (
                  <div key={item.title} className="bg-white rounded-xl p-5 border border-gray-100">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#D97642]/10 mb-4">
                      <i className={`${item.icon} text-xl text-[#D97642]`}></i>
                    </div>
                    <h3 className="text-sm font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Rate by Age Table */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Rate Reference</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Senior Life Insurance Rates by Age — Indianapolis 2026
              </h2>
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1A3A52] text-white">
                      <th className="px-5 py-4 text-left font-bold">Age</th>
                      <th className="px-5 py-4 text-left font-bold">Final Expense</th>
                      <th className="px-5 py-4 text-left font-bold">Simplified Issue</th>
                      <th className="px-5 py-4 text-left font-bold">Guaranteed Issue</th>
                      <th className="px-5 py-4 text-left font-bold">Best Carrier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { age: 60, fe: '$46/mo · $20K', si: '$88/mo · $50K', gi: '$62/mo · $15K', carrier: 'Mutual of Omaha' },
                      { age: 65, fe: '$58/mo · $20K', si: '$118/mo · $50K', gi: '$78/mo · $12K', carrier: 'Mutual of Omaha' },
                      { age: 70, fe: '$72/mo · $15K', si: '$98/mo · $25K', gi: '$88/mo · $12K', carrier: 'Mutual of Omaha' },
                      { age: 75, fe: '$98/mo · $15K', si: 'N/A', gi: '$108/mo · $10K', carrier: 'AIG / Gerber Life' },
                      { age: 80, fe: 'N/A', si: 'N/A', gi: '$138/mo · $10K', carrier: 'AIG / Gerber Life' },
                    ].map((row, i) => (
                      <tr key={row.age} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                        <td className="px-5 py-4 font-bold text-[#1A3A52]">Age {row.age}</td>
                        <td className="px-5 py-4 text-gray-700">{row.fe}</td>
                        <td className="px-5 py-4 text-gray-700">{row.si}</td>
                        <td className="px-5 py-4 text-gray-700">{row.gi}</td>
                        <td className="px-5 py-4 text-xs font-semibold text-[#D97642]">{row.carrier}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">Rates are estimates for good health class. Actual rates vary. April 2026.</p>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Senior Life Insurance Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">{faq.q}</h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-user-heart-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Senior Quote</h3>
                    <p className="text-xs text-white/70">Ages 60–85 · No exam · 24-hr approval</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No medical exam</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Any health condition</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ From $28/mo</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Senior Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · Indianapolis senior specialists</p>
              </div>
            </div>

            {/* Quick Reference */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-file-list-3-line text-[#D97642]"></i>
                Senior Coverage Quick Reference
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { label: 'Final Expense (no exam)', range: '$28–$98/mo · $10K–$35K', ages: 'Ages 50–80' },
                  { label: 'Simplified Issue Whole Life', range: '$88–$142/mo · $25K–$100K', ages: 'Ages 50–75' },
                  { label: 'Guaranteed Issue (no questions)', range: '$62–$152/mo · $7.5K–$15K', ages: 'Ages 50–85' },
                  { label: 'Term Life (exam may be required)', range: '$78–$140/mo · $100K–$500K', ages: 'Ages 60–70' },
                ].map((item, i) => (
                  <div key={i} className="py-2 border-b border-gray-100 last:border-0">
                    <p className="text-xs font-bold text-[#1A3A52]">{item.label}</p>
                    <p className="text-xs text-[#D97642] font-semibold mt-0.5">{item.range}</p>
                    <p className="text-xs text-gray-400">{item.ages}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free Senior Quote Call</h3>
                    <p className="text-xs text-white/70">15 minutes · No pressure · Indianapolis</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Free Senior Quote Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: 'Your age & any health conditions (Optional)',
                    submitButton: 'Confirm My Free Senior Quote Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/senior-life-insurance-indianapolis', label: 'Senior Life Insurance Indianapolis', icon: 'ri-user-heart-line' },
                  { to: '/final-expense-insurance-indianapolis', label: 'Final Expense Insurance Indianapolis', icon: 'ri-heart-pulse-line' },
                  { to: '/burial-insurance-indianapolis', label: 'Burial Insurance Indianapolis', icon: 'ri-shield-check-line' },
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Medical Exam Options', icon: 'ri-timer-flash-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Get Your Free Senior Life Insurance Quote — Indianapolis
          </h2>
          <p className="text-base text-white/75 mb-7">
            Ages 60–85 · No medical exam · Any health condition · From $28/month. Free, no obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#rate-finder" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-lg"></i>
              Find My Senior Rate
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Life Insurance for Seniors Over 60 Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Senior Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Free Senior Life Insurance Quote — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
